import React, { useContext, useState, useEffect } from "react";
import {
    List,
    Card,
    Accordion,
    Typography,
    AccordionBody,
    ListItem,
    ListItemPrefix,
} from "@material-tailwind/react";

import {
    TicketIcon,
    UserGroupIcon,
    Square2StackIcon,
    RectangleGroupIcon,
    CalendarIcon,
    MoonIcon,
    SunIcon,
    GlobeAltIcon,
    PhotoIcon,
} from "@heroicons/react/24/solid";

import {
    ChevronDownIcon,
    ArrowLeftStartOnRectangleIcon,
    Bars3Icon,
} from "@heroicons/react/24/outline";

import logo from "../../assets/main-website-logo-trans.png";
import { AuthContext } from "../../App";

function SidebarDark({ isOpen, onClose, collapsed, toggleCollapsed, theme, toggleTheme }) {
    const [open, setOpen] = useState(0);
    const { logout } = useContext(AuthContext);

    const handleOpen = (value) => setOpen(open === value ? 0 : value);
    const handleLogout = async () => await logout();

    const LIST_ITEM_STYLES =
        "text-gray-500 hover:text-white focus:text-white active:text-white hover:bg-opacity-20 focus:bg-opacity-20 active:bg-opacity-20";

    return (
        <Card
            className={`
                ${theme === "dark" ? "bg-gray-900 text-white" : "bg-white text-gray-900"}
                ${collapsed ? "w-20" : "w-72"} 
                min-h-screen shadow-md transition-all duration-300 rounded-none z-50 
                fixed md:static top-0 left-0 transform 
                ${isOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
                flex flex-col justify-between
            `}
        >
            <div className="flex-1 overflow-y-auto">
                {/* Logo & Collapse Button */}
                <div className="flex items-center justify-between p-4">
                    {!collapsed && (
                        <img src={logo} alt="brand" className="h-10 w-10" />
                    )}
                    {collapsed && (
                        <img src={logo} alt="brand" className="h-8 w-8 mx-auto" />
                    )}
                    {!collapsed && (
                        <Typography className="ml-2 text-sm font-bold text-gray-300">
                            Federation Skating Club Admin
                        </Typography>
                    )}
                    <button
                        onClick={toggleCollapsed}
                        className="text-gray-400 hover:text-white focus:outline-none"
                    >
                        <Bars3Icon className="h-5 w-5" />
                    </button>
                </div>

                <hr className="my-2 border-gray-800" />

                {/* Main Navigation */}
                <List>
                    <a href="/dashboard" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <Square2StackIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && "Dashboard"}
                        </ListItem>
                    </a>
                    {/* Store Section */}
                    <Accordion open={open === 2}>
                        <ListItem
                            selected={open === 2}
                            data-selected={open === 2}
                            onClick={() => handleOpen(2)}
                            className={`hover:bg-opacity-20 data-[selected=true]:bg-gray-50/20 ${LIST_ITEM_STYLES}`}
                        >
                            <ListItemPrefix>
                                <RectangleGroupIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && (
                                <>
                                    <Typography className="mr-auto font-normal text-inherit">
                                        Store
                                    </Typography>
                                    <ChevronDownIcon
                                        strokeWidth={3}
                                        className={`ml-auto h-4 w-4 transition-transform ${open === 2 ? "rotate-180" : ""}`}
                                    />
                                </>
                            )}
                        </ListItem>
                        <AccordionBody className="py-1">
                            <List className="p-0">
                                <ListItem className={`pl-14 pr-4 py-2 rounded-md cursor-pointer ${LIST_ITEM_STYLES}`}>
                                    Products
                                </ListItem>
                                <ListItem className={`pl-14 pr-4 py-2 rounded-md cursor-pointer ${LIST_ITEM_STYLES}`}>
                                    Sales
                                </ListItem>
                            </List>
                        </AccordionBody>
                    </Accordion>

                    <a href="/dashboard/events" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <CalendarIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && "Events"}
                        </ListItem>
                    </a>

                    <a href="/dashboard/coaches" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <UserGroupIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && "Coaches"}
                        </ListItem>
                    </a>

                    <a href="/dashboard/boardmembers" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <UserGroupIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && "Board Members"}
                        </ListItem>
                    </a>

                    <a href="/dashboard/faqs" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <TicketIcon className="h-5 w-5"/>
                            </ListItemPrefix>
                            {!collapsed && "FAQs"}
                        </ListItem>
                    </a>

                    <a href="/dashboard/homepagecarousel" className="no-underline">
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <PhotoIcon className="h-5 w-5" />
                            </ListItemPrefix>
                            {!collapsed && "Home Page Carousel"}
                        </ListItem>
                    </a>
                </List>
            </div>

            {/* Footer */}
            <div className="mb-4">
                <hr className="my-2 border-gray-800"/>
                <List>

                    <a
                        href="https://www.federationskatingclub.ca/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="no-underline"
                    >
                        <ListItem className={LIST_ITEM_STYLES}>
                            <ListItemPrefix>
                                <GlobeAltIcon className="h-5 w-5" />
                            </ListItemPrefix>
                            {!collapsed && "View Website"}
                        </ListItem>
                    </a>

                    <ListItem onClick={handleLogout} className={LIST_ITEM_STYLES}>
                        <ListItemPrefix>
                            <ArrowLeftStartOnRectangleIcon strokeWidth={2.5} className="h-5 w-5" />
                        </ListItemPrefix>
                        {!collapsed && "Sign Out"}
                    </ListItem>
                </List>
            </div>
        </Card>
    );
}

export function Sidebar() {
    const [isOpen, setIsOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [theme, setTheme] = useState("dark");

    useEffect(() => {
        document.documentElement.classList.toggle("dark", theme === "dark");
    }, [theme]);

    const toggleCollapsed = () => setCollapsed((prev) => !prev);
    const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

    return (
        <>
            {/* Mobile Toggle Button */}
            <button
                className="md:hidden fixed top-4 left-2 z-50 bg-gray-800 text-white p-2 rounded-md"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                ☰
            </button>

            <SidebarDark
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                collapsed={collapsed}
                toggleCollapsed={toggleCollapsed}
                theme={theme}
                toggleTheme={toggleTheme}
            />

            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    onClick={() => setIsOpen(false)}
                    className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden transition-opacity duration-300"
                />
            )}
        </>
    );
}

export default Sidebar;
